import React from 'react';
import { Button, Flex, Heading, Text, TextField, View } from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import RentalSegmentPicker, { useRentalSegments } from '../components/rentalsegmentpicker';
import { api3Get, api3Post } from '../api/api3Client';
import { getPortalUser } from '../auth/portalSession';
import { usePortalRole } from '../hooks/usePortalRole';

const CANCELLABLE_RES_STATUSES = new Set([
  'pre',
  'deposit',
  'price',
  'remaining',
  'extension',
]);

export default function Reservationinsurancecard({ resId, resStatus, firstCheckFee }) {
  const [portalUser, setPortalUser] = React.useState(null);
  const { isAdmin } = usePortalRole(portalUser);
  const [isLoading, setIsLoading] = React.useState(false);
  const [insuranceUrl, setInsuranceUrl] = React.useState('');
  const [error, setError] = React.useState('');
  const [bufferMsg, setBufferMsg] = React.useState('');
  const [bufferBusy, setBufferBusy] = React.useState(false);
  const [optionCMsg, setOptionCMsg] = React.useState('');
  const [optionCBusy, setOptionCBusy] = React.useState(false);
  const [daysMsg, setDaysMsg] = React.useState('');
  const [daysBusy, setDaysBusy] = React.useState(false);
  const [insuranceDays, setInsuranceDays] = React.useState('1');

  React.useEffect(() => {
    let cancelled = false;
    getPortalUser()
      .then((u) => {
        if (!cancelled) setPortalUser(u);
      })
      .catch(() => {
        if (!cancelled) setPortalUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const {
    segments,
    selectedLedgerVersion,
    setSelectedLedgerVersion,
    loadError,
    isLoading: segmentsLoading,
  } = useRentalSegments(resId);

  async function getInsuranceCardUrl() {
    if (!resId) {
      setError('Reservation id is required');
      return;
    }
    setIsLoading(true);
    setError('');
    setInsuranceUrl('');
    try {
      const query = selectedLedgerVersion ? { ledgerVersion: selectedLedgerVersion } : undefined;
      const res = await api3Get(
        `/reservations/${encodeURIComponent(resId)}/insurance-card`,
        query,
      );
      if (res?.status === 'success' && res?.url) {
        setInsuranceUrl(res.url);
      } else {
        setError(res?.message || res?.status || 'Insurance card not available');
      }
    } catch (e) {
      setError(e?.message || 'There was an error processing your request');
    } finally {
      setIsLoading(false);
    }
  }

  async function refundInsuranceBufferDay() {
    if (!resId || !isAdmin) return;
    const ok = window.confirm(
      'Refund unused insurance buffer day(s) (premium + service fee) for reservation '
        + resId
        + '? This is for Option C combined checkout only and is idempotent.',
    );
    if (!ok) return;
    setBufferBusy(true);
    setBufferMsg('');
    try {
      const res = await api3Post(
        `/reservations/${encodeURIComponent(resId)}/insurance/period-change`,
        { mode: 'buffer_refund', force: true },
      );
      if (res?.status === 'success') {
        if (res.skipped) {
          setBufferMsg(`Skipped: ${res.reason || 'not eligible'}`);
        } else {
          setBufferMsg(
            `Refunded $${Number(res.totalRefundUsd || 0).toFixed(2)} `
              + `(${res.bufferDays || 0} day(s)).`,
          );
        }
      } else {
        setBufferMsg(res?.message || res?.status || 'Buffer refund failed');
      }
    } catch (e) {
      setBufferMsg(e?.message || 'Buffer refund request failed');
    } finally {
      setBufferBusy(false);
    }
  }

  async function refundInsuranceDays() {
    if (!resId || !isAdmin) return;
    const days = parseInt(String(insuranceDays || '').trim(), 10);
    if (!Number.isFinite(days) || days < 1) {
      setDaysMsg('Enter a positive number of insurance days to refund');
      return;
    }
    const ok = window.confirm(
      `Refund ${days} insurance day(s) (premium + service fee) for reservation `
        + resId
        + '?\n\n'
        + 'Use this after cancel when rental was refunded but AGO insurance was withheld '
        + 'because coverage may have started. Amount is capped at remaining refundable days.',
    );
    if (!ok) return;
    setDaysBusy(true);
    setDaysMsg('');
    try {
      const res = await api3Post(
        `/reservations/${encodeURIComponent(resId)}/insurance/period-change`,
        { mode: 'insurance_days_refund', days },
      );
      if (res?.status === 'success') {
        if (res.skipped) {
          setDaysMsg(`Skipped: ${res.reason || 'not eligible'}`);
        } else {
          setDaysMsg(
            `Refunded $${Number(res.totalRefundUsd || 0).toFixed(2)} `
              + `(${res.days || days} day(s); ${res.remainingDays ?? '?'} remaining).`,
          );
        }
      } else {
        setDaysMsg(res?.message || res?.status || 'Insurance days refund failed');
      }
    } catch (e) {
      setDaysMsg(e?.message || 'Insurance days refund request failed');
    } finally {
      setDaysBusy(false);
    }
  }

  async function refundOptionCCombinedFull(keepFirstCheckFee) {
    if (!resId || !isAdmin) return;
    const statusKey = String(resStatus || '').trim().toLowerCase();
    const resStillOpen = CANCELLABLE_RES_STATUSES.has(statusKey);
    const keepUsd = Number(firstCheckFee && firstCheckFee.amountUsd);
    const feeLine = keepFirstCheckFee
      ? (
        'This keeps the $' + keepUsd.toFixed(2) + ' verification fee '
        + '(the same first-check premium a before-start cancel keeps) and refunds the rest, '
        + 'including the service fee. It still runs after the trip has started or closed.\n\n'
      )
      : (
        'This refunds the remaining balance, including the verification fee when one applies.\n\n'
      );
    const openWarning = resStillOpen
      ? (
        'This reservation is still active (status: '
        + (resStatus || 'unknown')
        + ').\n\n'
        + 'Option C only refunds the combined insurance / Stripe balance. It will NOT cancel '
        + 'the trip or return rental credits/invoices.\n\n'
        + 'For a full unwind, use Cancel reservation (full refund + card) first.\n\n'
      )
      : '';
    const ok = window.confirm(
      openWarning
      + 'Refund remaining Option C combined checkout balance for reservation '
      + resId
      + '?\n\n'
      + feeLine
      + 'This unwinds remaining Stripe PaymentIntent balance and the FORE transfer '
      + '(or cancels a pending FORE transfer). It is idempotent.',
    );
    if (!ok) return;
    setOptionCBusy(true);
    setOptionCMsg('');
    try {
      const res = await api3Post(
        `/reservations/${encodeURIComponent(resId)}/option-c-full-refund`,
        {
          reason: 'admin_portal_option_c_full_refund',
          keepFirstCheckFee: !!keepFirstCheckFee,
        },
      );
      if (res?.status === 'success') {
        const handled = res.optionCHandled === true ? 'optionCHandled=true' : 'optionCHandled=false';
        const kept = Number(res.keepFirstCheckFeeUsd || 0);
        const keepNote = res.keepFirstCheckFeeRequested
          ? (
            kept > 0
              ? `, kept verification fee $${kept.toFixed(2)}`
              : `, verification fee not kept (${res.keepFirstCheckFeeReason || 'not applicable'})`
          )
          : '';
        if (res.skipped) {
          setOptionCMsg(
            `Skipped (${handled}): ${res.reason || 'not eligible'}`,
          );
        } else {
          setOptionCMsg(
            `Success (${handled}): refundCount=${res.refundCount ?? 0}`
              + (res.caseId != null ? `, caseId=${res.caseId}` : '')
              + keepNote,
          );
        }
      } else {
        setOptionCMsg(res?.message || res?.status || 'Option C full refund failed');
      }
    } catch (e) {
      setOptionCMsg(e?.message || 'Option C full refund request failed');
    } finally {
      setOptionCBusy(false);
    }
  }

  if (isLoading || segmentsLoading) {
    return <Loadingmini />;
  }

  const adminBusy = bufferBusy || optionCBusy || daysBusy;

  return (
    <Flex direction="column" gap="0.5rem" style={{ marginTop: '1rem' }}>
      <Heading level={4}>Insurance card</Heading>
      <RentalSegmentPicker
        segments={segments}
        selectedLedgerVersion={selectedLedgerVersion}
        onChange={setSelectedLedgerVersion}
        loadError={loadError}
      />
      {error ? (
        <View>
          <Text>{error}</Text>
          <Button onClick={getInsuranceCardUrl}>Try again</Button>
        </View>
      ) : insuranceUrl ? (
        <Text>
          <a href={insuranceUrl} target="_blank" rel="noreferrer">
            Click to download insurance card
          </a>
        </Text>
      ) : (
        <Button onClick={getInsuranceCardUrl}>View insurance card</Button>
      )}
      {isAdmin ? (
        <View>
          <Button
            onClick={refundInsuranceBufferDay}
            isDisabled={adminBusy}
            variation="link"
          >
            {bufferBusy ? 'Refunding…' : 'Refund insurance buffer day'}
          </Button>
          {bufferMsg ? <Text>{bufferMsg}</Text> : null}
          <Flex alignItems="flex-end" gap="0.5rem" wrap="wrap">
            <TextField
              label="Insurance days to refund"
              type="number"
              min={1}
              value={insuranceDays}
              onChange={(e) => setInsuranceDays(e.target.value)}
              width="10rem"
            />
            <Button
              onClick={refundInsuranceDays}
              isDisabled={adminBusy}
              variation="link"
            >
              {daysBusy ? 'Refunding…' : 'Refund insurance days'}
            </Button>
          </Flex>
          {daysMsg ? <Text>{daysMsg}</Text> : null}
          {firstCheckFee && firstCheckFee.applicable && Number(firstCheckFee.amountUsd) > 0 ? (
            <Button
              onClick={() => refundOptionCCombinedFull(true)}
              isDisabled={adminBusy}
              variation="link"
            >
              {optionCBusy
                ? 'Refunding…'
                : (
                  'Refund remaining Option C balance, keep the $'
                  + Number(firstCheckFee.amountUsd).toFixed(2)
                  + ' verification fee'
                )}
            </Button>
          ) : null}
          <Button
            onClick={() => refundOptionCCombinedFull(false)}
            isDisabled={adminBusy}
            variation="link"
          >
            {optionCBusy
              ? 'Refunding…'
              : (
                firstCheckFee && firstCheckFee.applicable
                  ? 'Refund remaining Option C balance, including the verification fee'
                  : 'Refund remaining Option C balance'
              )}
          </Button>
          {optionCMsg ? <Text>{optionCMsg}</Text> : null}
        </View>
      ) : null}
    </Flex>
  );
}
